<template>
  <div class="flex flex-col flex-1">
    <Component
      :is="stickySearch ? 'MAffix' : 'div'"
      v-if="!disabled"
      :offset-top="offsetTop"
      @change="searchAffixed = $event"
    >
      <div :class="{ 'pt-2': searchAffixed }">
        <div class="text-right">
          <MButton
            :class="{ invisible: disabled }"
            variant="neutral"
            :trigger-text="`${$t('add')} ${$tc('relation')}`"
            @click="addNewRelation"
          >
            {{ $tc('link') }}
            {{ $tc(moduleName === $constants.CMDB ? 'ci' : 'asset', 2) }}
          </MButton>
          <MDivider />
        </div>
      </div>
    </Component>
    <div class="flex flex-col">
      <AssetList
        :key="renderCount"
        ref="destinationList"
        client-side-pagination
        :scrollable="false"
        :module-name="moduleName"
        :has-actions="!disabled"
        :show-no-data="false"
        :hidden-columns="hiddenColumns"
        :fetch-fn="fetchRelations"
        :linkable="false"
      >
        <template
          v-if="!disabled"
          v-slot:action="{ item, stickAction, unstickAction }"
        >
          <FlotoDeleteBtn
            v-if="item.relationType !== 'child'"
            :message="$t('confirm_remove_item', { item: $tc('relation') })"
            @trigger="stickAction"
            @cancel="unstickAction"
            @confirm="removeRelation(item)"
          >
            <MTooltip>
              <template v-slot:trigger>
                <MIcon name="unlink" size="lg" />
              </template>
              {{ $t('remove') }}
            </MTooltip>
          </FlotoDeleteBtn>
        </template>
      </AssetList>
    </div>
    <FlotoDrawer :open="showDrawer" width="65%" @hide="hideDrawer">
      <template v-slot:title>
        {{ $tc('link') }}
        {{ $tc(moduleName === $constants.CMDB ? 'ci' : 'asset', 2) }}
      </template>
      <AssetList
        ref="list"
        client-side-pagination
        selectable
        :module-name="moduleName"
        :searchable="false"
        :fetch-fn="fetchEligableRelations"
        :linkable="false"
        @selection-change="setSelectedItems"
      />
      <template v-slot:actions="{ hide }">
        <MButton
          :disabled="selectedItems.length === 0"
          outline
          class="mr-2"
          :loading="processing"
          @click="handleAddRelations"
        >
          {{ $t('link') }}
        </MButton>
        <MButton variant="default" @click="hide">
          {{ $t('cancel') }}
        </MButton>
      </template>
    </FlotoDrawer>
  </div>
</template>

<script>
import AssetList from '@components/item-selection-list/asset-list'
import { transformAssetForList } from '@data/asset'
import { LicenseComputed } from '@state/modules/license'
import { getMyAssetApi } from '@modules/asset/asset-api'
import {
  bulkAddRelationApi,
  getRelationsApi,
  deleteRelationApi,
} from './relation-api'

export default {
  name: 'RelationContainer',
  components: { AssetList },
  props: {
    resourceId: {
      type: Number,
      required: true,
    },
    serviceCatalogId: {
      type: Number,
      default: undefined,
    },
    moduleName: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    stickySearch: { type: Boolean, default: false },
    offsetTop: { type: Number, default: 0 },
  },
  data() {
    this.hiddenColumns = ['assignedUserIds']
    return {
      renderCount: 1,
      showDrawer: false,
      selectedItems: [],
      processing: false,
      alreadyRelatedIds: [],
      searchAffixed: false,
    }
  },
  computed: {
    ...LicenseComputed,
  },
  methods: {
    removeRelation(item) {
      deleteRelationApi(item.relationId).then(() => {
        this.$emit('relation-removed', {
          sourceModuleName: this.sourceModuleName,
          destinationModuleName: this.destinationModuleName,
        })
        this.renderCount += 1
      })
    },
    fetchRelations(_, criterias, limit, offset) {
      return getRelationsApi(
        this.$constants.REQUEST,
        this.resourceId,
        this.moduleName,
        'relation',
        limit,
        offset,
        this.isApproval ? { isApproval: true } : {}
      ).then((data) => {
        data.items = data.items.map(transformAssetForList)
        return data.items
      })
    },
    fetchEligableRelations(targetModuleName, criterias, limit, offset) {
      const additionalParams = {
        model: this.$constants.REQUEST,
        id: this.resourceId,
        ...(this.moduleName === this.$constants.CMDB
          ? {
              linkCiCall: true,
            }
          : {
              linkAssetCall: true,
            }),
        ...(this.serviceCatalogId
          ? { serviceCatalogId: this.serviceCatalogId }
          : {}),
      }
      return getMyAssetApi(this.moduleName, additionalParams).then((data) => {
        return (data.objectList || []).map(
          (i) => transformAssetForList({ ...i, relationId: 'fake_id' }) // add fake relation id for combined ids
        )
      })
    },
    addNewRelation() {
      setTimeout(() => {
        this.showDrawer = true
      }, 250)
    },
    handleAddRelations() {
      if (this.processing) {
        return
      }
      this.processing = true
      bulkAddRelationApi(
        this.$constants.REQUEST,
        this.resourceId,
        this.selectedItems.map(({ id }) => id),
        this.moduleName
      )
        .then((data) => {
          // immediate display identify as proble tag
          this.renderCount += 1
          this.$emit('refresh')
          this.hideDrawer()
        })
        .finally(() => {
          setTimeout(() => {
            this.processing = false
          }, 250)
        })
    },
    hideDrawer() {
      this.selectedItems = []
      this.showDrawer = false
    },
    setSelectedItems(items) {
      this.selectedItems = items
    },
  },
}
</script>
